import {
  Activity,
  AlertTriangle,
  BarChart3,
  Database,
  LogOut,
  Menu,
  ServerCog,
  Wrench,
} from 'lucide-react';
import * as React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/utils';

interface AdminNavItem {
  to: string;
  label: string;
  Icon: typeof Activity;
  end?: boolean;
}

const ADMIN_NAV: readonly AdminNavItem[] = [
  { to: '/admin', label: 'Dashboard', Icon: Activity, end: true },
  { to: '/admin/usage', label: 'Usage', Icon: BarChart3 },
  { to: '/admin/data', label: 'Data', Icon: Database },
  { to: '/admin/errors', label: 'Errors', Icon: AlertTriangle },
  { to: '/admin/system', label: 'System', Icon: ServerCog },
];

function AdminBrand(): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 px-4 py-4">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-accent-foreground"
      >
        <Wrench className="h-4 w-4" />
      </span>
      <span className="text-subheading font-semibold tracking-tight">Admin</span>
    </div>
  );
}

function AdminNavList({ onNavigate }: { onNavigate?: () => void }): React.JSX.Element {
  return (
    <nav aria-label="Admin" className="flex flex-col gap-1">
      {ADMIN_NAV.map(({ to, label, Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end ?? false}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-caption font-medium transition-colors',
              isActive
                ? 'bg-accent/15 text-accent'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

interface SignOutButtonProps {
  onSignOut: () => void;
}

function SignOutButton({ onSignOut }: SignOutButtonProps): React.JSX.Element {
  return (
    <Button type="button" variant="outline" className="w-full justify-start" onClick={onSignOut}>
      <LogOut className="h-4 w-4" aria-hidden="true" />
      <span className="ml-2">Sign out</span>
    </Button>
  );
}

export function AdminShell(): React.JSX.Element {
  const { status, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleSignOut = React.useCallback(() => {
    setMobileOpen(false);
    void (async () => {
      try {
        await logout();
      } finally {
        navigate('/admin/login', { replace: true });
      }
    })();
  }, [logout, navigate]);

  const isAuthed = status === 'authenticated';

  return (
    <div className="flex min-h-full bg-background text-foreground">
      <aside
        aria-label="Admin sidebar"
        className="hidden w-60 shrink-0 flex-col border-r border-border bg-surface md:flex"
      >
        <AdminBrand />
        <Separator />
        <div className="flex-1 overflow-y-auto p-3">
          <AdminNavList />
        </div>
        {isAuthed ? (
          <div className="p-3">
            <SignOutButton onSignOut={handleSignOut} />
          </div>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface px-4 md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Open admin navigation menu"
              >
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="sr-only">Admin navigation</SheetTitle>
              <SheetDescription className="sr-only">
                Admin sections and sign-out for train-tracks.
              </SheetDescription>
              <AdminBrand />
              <Separator />
              <div className="flex flex-1 flex-col gap-3 p-3">
                <AdminNavList
                  onNavigate={() => {
                    setMobileOpen(false);
                  }}
                />
                {isAuthed ? <SignOutButton onSignOut={handleSignOut} /> : null}
              </div>
            </SheetContent>
          </Sheet>
          <span
            aria-hidden="true"
            className="flex h-7 w-7 items-center justify-center rounded-md bg-accent text-accent-foreground"
          >
            <Wrench className="h-4 w-4" />
          </span>
          <span className="text-subheading font-semibold tracking-tight">Admin</span>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
